/**
 * Collection Infinite Scroll Component
 * UPDATED to match your latest Liquid structure changes
 * - Updated PREVENTA markers with info/close icons
 * - Reorganized mobile layout structure
 * - Hide sold-out products properly
 * - Simplified desktop layout (no thumbnail)
 */
class CollectionInfiniteScroll {
  constructor(containerId) {
    this.containerId = containerId
    this.container = document.getElementById(containerId)
    if (!this.container) {
      console.error("Collection container not found:", containerId)
      return
    }

    // Initialize configuration from data attributes
    this.config = this.getConfiguration()

    // Initialize state
    this.state = {
      currentPage: 1,
      isLoading: false,
      hasMoreProducts: true,
    }

    this.lastAddedProductId = null
    this.lastAddedVariantId = null

    // Get DOM elements
    this.elements = this.getDOMElements()

    // Initialize component
    this.init()
  }

  /**
   * Get configuration from data attributes
   */
  getConfiguration() {
    return {
      blockId: this.container.getAttribute("data-block-id"),
      collectionHandle: this.container.getAttribute("data-collection-handle"),
      productsPerPage: Number.parseInt(this.container.getAttribute("data-products-per-page"), 10),
      productsPerRow: Number.parseInt(this.container.getAttribute("data-products-per-row"), 10),
      currencySymbol: this.container.getAttribute("data-currency-symbol") || "$",
    }
  }

  /**
   * Get DOM elements
   */
  getDOMElements() {
    return {
      productsGrid: this.container.querySelector("[data-products-grid]"),
      statusElement: this.container.querySelector("[data-infinite-scroll-status]"),
      loadingElement: this.container.querySelector("[data-loading]"),
      errorElement: this.container.querySelector("[data-error]"),
      skeletonContainer: this.container.querySelector("[data-skeleton-container]"),
    }
  }

  /**
   * Initialize the component
   */
  init() {
    console.log("🚀 Infinite scroll initialized:", this.config)

    // Bind methods to preserve context
    this.handleAddToCart = this.handleAddToCart.bind(this)
    this.handleToggleClick = this.handleToggleClick.bind(this)
    this.onScroll = this.debounce(this.onScroll.bind(this), 100)

    // Add event listeners
    this.addEventListeners()

    // Initial setup
    this.addCartListeners()

    // Debug cart buttons (remove in production)
    setTimeout(() => {
      this.debugCartButtons()
    }, 1000)

    // Initial scroll check
    setTimeout(() => {
      this.onScroll()
    }, 500)
  }

  /**
   * Add global event listeners
   */
  addEventListeners() {
    window.addEventListener("scroll", this.onScroll)
    window.addEventListener("resize", this.onScroll)
    window.addEventListener("resize", () => {
      this.updateAllMarcadorProperties()
    })

    // Global retry function
    window.retryLoadProducts = () => {
      this.elements.errorElement.style.display = "none"
      this.loadMoreProducts()
    }
  }

  /**
   * Debounce utility function
   */
  debounce(func, wait) {
    let timeout
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout)
        func(...args)
      }
      clearTimeout(timeout)
      timeout = setTimeout(later, wait)
    }
  }

  /**
   * Handle add to cart button clicks
   */
  handleAddToCart(event) {
    event.preventDefault()
    event.stopPropagation()

    const button = event.currentTarget
    if (!button || button.disabled) return

    const variantId = button.getAttribute("data-variant-id")
    const productId = button.getAttribute("data-product-id")
    const quantity = button.getAttribute("data-quantity") || "1"
    const productTitle = button.getAttribute("data-product-title")
    const isBuyNow = button.classList.contains("btn-comprar-ahora")

    console.log("🛒 Button clicked:", {
      variantId,
      productId,
      quantity,
      productTitle,
      isBuyNow,
    })

    if (!isBuyNow) {
      this.lastAddedProductId = productId
      this.lastAddedVariantId = variantId
    }

    const image = button.querySelector(".btn-quick-add__image, .btn-quick-add-mobile__image")
    const text = button.querySelector(".btn-comprar-ahora__text")
    const originalText = text ? text.textContent : ""

    // Set loading state
    button.classList.add("loading")
    button.disabled = true
    if (text) text.textContent = isBuyNow ? "Procesando..." : "Agregando..."

    if (isBuyNow) {
      this.handleBuyNow(variantId, quantity)
    } else {
      this.handleRegularAddToCart(variantId, quantity, button, text, originalText)
    }
  }

  /**
   * Handle buy now functionality
   */
  handleBuyNow(variantId, quantity) {
    const form = document.createElement("form")
    form.method = "POST"
    form.action = "/cart/add"
    form.style.display = "none"

    // Add variant ID
    const variantInput = document.createElement("input")
    variantInput.type = "hidden"
    variantInput.name = "id"
    variantInput.value = variantId
    form.appendChild(variantInput)

    // Add quantity
    const quantityInput = document.createElement("input")
    quantityInput.type = "hidden"
    quantityInput.name = "quantity"
    quantityInput.value = quantity
    form.appendChild(quantityInput)

    // Add return URL to checkout
    const returnInput = document.createElement("input")
    returnInput.type = "hidden"
    returnInput.name = "return_to"
    returnInput.value = "/checkout"
    form.appendChild(returnInput)

    // Submit form
    document.body.appendChild(form)
    form.submit()
  }

  /**
   * Handle regular add to cart with theme-specific events
   */
  handleRegularAddToCart(variantId, quantity, button, text, originalText) {
    const formData = new FormData()
    formData.append("id", variantId)
    formData.append("quantity", quantity)

    console.log("📦 Submitting to cart with variant ID:", variantId)

    fetch("/cart/add.js", {
      method: "POST",
      body: formData,
      headers: {
        "X-Requested-With": "XMLHttpRequest",
      },
    })
      .then((response) => {
        console.log("📦 Cart response status:", response.status)
        if (!response.ok) {
          return response.text().then((text) => {
            throw new Error(`HTTP error! status: ${response.status}, body: ${text}`)
          })
        }
        return response.json()
      })
      .then((data) => {
        console.log("✅ Successfully added to cart:", data)

        // Set success state
        button.classList.remove("loading")
        button.classList.add("success")
        if (text) text.textContent = "¡Agregado!"

        // THEME-SPECIFIC: Dispatch the cart update event that your theme expects
        this.dispatchThemeCartUpdateEvent(data)

        // Reset button after 2 seconds
        setTimeout(() => {
          button.classList.remove("success")
          button.disabled = false
          if (text) text.textContent = originalText
        }, 2000)
      })
      .catch((error) => {
        console.error("❌ Error adding to cart:", error)

        // Set error state
        button.classList.remove("loading")
        button.classList.add("error")
        if (text) text.textContent = "Error"

        // Dispatch error event
        this.dispatchThemeCartUpdateEvent(null, error)

        // Reset button after 2 seconds
        setTimeout(() => {
          button.classList.remove("error")
          button.disabled = false
          if (text) text.textContent = originalText
        }, 2000)
      })
  }

  /**
   * Dispatch theme-specific cart update events
   */
  dispatchThemeCartUpdateEvent(cartData = null, error = null) {
    console.log("📡 Dispatching theme cart update events...")

    // Fetch fresh cart data to ensure accuracy
    fetch("/cart.js")
      .then((response) => response.json())
      .then((cart) => {
        console.log("🔄 Fresh cart data:", cart)

        // Create the cart update event data structure that matches your theme
        const eventData = {
          data: {
            cart: cart,
            items: cart.items,
            itemCount: cart.item_count,
            totalPrice: cart.total_price,
            didError: !!error,
            error: error,
            source: "collection-infinite-scroll",
            productId: this.lastAddedProductId,
            variantId: this.lastAddedVariantId,
          },
        }

        // Method 1: Try to dispatch the exact event your theme uses
        try {
          const cartUpdateEvent = new CustomEvent("theme:cart-update", {
            detail: eventData,
            bubbles: true,
            cancelable: true,
          })

          document.dispatchEvent(cartUpdateEvent)
          console.log("✅ Dispatched theme:cart-update event")
        } catch (e) {
          console.warn("⚠️ Could not dispatch theme:cart-update:", e)
        }

        // Method 2: Try common theme event names
        const themeEventNames = [
          "cart:updated",
          "cart:update",
          "cart:change",
          "cart:add",
          "cart:item-added",
          "theme:cart:updated",
          "theme:cart:update",
          "cartUpdate",
          "cartUpdated",
        ]

        themeEventNames.forEach((eventName) => {
          try {
            const event = new CustomEvent(eventName, {
              detail: eventData,
              bubbles: true,
              cancelable: true,
            })

            document.dispatchEvent(event)
            console.log(`📡 Dispatched ${eventName}`)
          } catch (e) {
            console.warn(`⚠️ Could not dispatch ${eventName}:`, e)
          }
        })

        // Method 3: Try to trigger theme-specific cart update methods
        this.triggerThemeCartMethods(cart)
      })
      .catch((error) => {
        console.error("❌ Error fetching cart for events:", error)
      })
  }

  /**
   * Try to trigger theme-specific cart update methods
   */
  triggerThemeCartMethods(cart) {
    console.log("🔧 Triggering theme cart methods...")

    // Method 1: Look for theme object and cart methods
    const themeMethods = [
      () => window.theme?.cart?.update?.(cart),
      () => window.theme?.cartDrawer?.update?.(cart),
      () => window.theme?.miniCart?.update?.(cart),
      () => window.theme?.updateCart?.(cart),
      () => window.theme?.refreshCart?.(),
    ]

    themeMethods.forEach((method, index) => {
      try {
        method()
        console.log(`✅ Theme method ${index + 1} executed`)
      } catch (e) {
        console.log(`ℹ️ Theme method ${index + 1} not available`)
      }
    })

    // Method 2: Look for global cart update functions
    const globalMethods = [
      () => window.updateCart?.(cart),
      () => window.refreshCart?.(cart),
      () => window.cartUpdate?.(cart),
      () => window.cartDrawerUpdate?.(cart),
    ]

    globalMethods.forEach((method, index) => {
      try {
        method()
        console.log(`✅ Global method ${index + 1} executed`)
      } catch (e) {
        console.log(`ℹ️ Global method ${index + 1} not available`)
      }
    })

    // Method 3: Try to find and update cart components directly
    this.updateCartComponents(cart)
  }

  /**
   * Update cart components directly
   */
  updateCartComponents(cart) {
    console.log("🎯 Updating cart components directly...")

    // Look for cart drawer components
    const cartComponents = [
      document.querySelector("cart-drawer"),
      document.querySelector("mini-cart"),
      document.querySelector("cart-sidebar"),
      document.querySelector("[data-cart-drawer]"),
      document.querySelector(".cart-drawer"),
    ].filter(Boolean)

    cartComponents.forEach((component, index) => {
      try {
        // Try common update methods
        if (component.update && typeof component.update === "function") {
          component.update(cart)
          console.log(`✅ Updated cart component ${index + 1} via update()`)
        } else if (component.refresh && typeof component.refresh === "function") {
          component.refresh()
          console.log(`✅ Refreshed cart component ${index + 1} via refresh()`)
        } else {
          // Try to dispatch event directly on the component
          component.dispatchEvent(
            new CustomEvent("cart:update", {
              detail: { cart },
              bubbles: true,
            }),
          )
          console.log(`✅ Dispatched event on cart component ${index + 1}`)
        }
      } catch (e) {
        console.log(`ℹ️ Could not update cart component ${index + 1}:`, e)
      }
    })

    // Update cart count in header
    this.updateHeaderCartCount(cart)
  }

  /**
   * Update cart count in header
   */
  updateHeaderCartCount(cart) {
    const cartSelectors = [
      "[data-cart-count]",
      "[data-cart-item-count]",
      ".cart-bubble",
      ".cart-bubble__text",
      ".header-actions__cart-icon .cart-bubble",
      ".header-actions__cart-icon .cart-bubble__text",
      ".cart-count",
      ".cart-count-bubble",
      ".cart__count",
      ".cart-counter",
      ".cart-item-count",
      ".cart-quantity",
      ".cart-total-items",
      "[data-cart-quantity]",
      "header-actions .cart-bubble",
      "header-actions [data-cart-count]",
      ".header__column--right .cart-bubble",
      ".site-header .cart-count",
      "#cart-count",
      ".js-cart-count",
    ]

    cartSelectors.forEach((selector) => {
      try {
        const elements = document.querySelectorAll(selector)
        elements.forEach((element) => {
          if (element) {
            element.textContent = cart.item_count
            element.innerHTML = cart.item_count
            element.setAttribute("data-cart-count", cart.item_count)
            element.setAttribute("data-cart-item-count", cart.item_count)

            // Apply styling for header cart bubble
            if (element.closest(".header") || element.closest("header")) {
              Object.assign(element.style, {
                color: "#fff !important",
                backgroundColor: "#000",
                visibility: "visible",
                opacity: "1",
                display: "block",
                fontSize: "14px",
                fontWeight: "600",
                zIndex: "999",
              })
            }

            // Handle cart bubble visibility
            const cartBubble =
              element.closest(".cart-bubble") || (element.classList.contains("cart-bubble") ? element : null)
            if (cartBubble && cartBubble.closest(".header")) {
              if (cart.item_count > 0) {
                Object.assign(cartBubble.style, {
                  display: "block",
                  visibility: "visible",
                  opacity: "1",
                  color: "#fff",
                  backgroundColor: "#000",
                  border: "1px solid #2d2e2c",
                  borderRadius: "50%",
                  minWidth: "20px",
                  minHeight: "20px",
                  textAlign: "center",
                  lineHeight: "18px",
                  fontSize: "12px",
                  fontWeight: "700",
                  zIndex: "1000",
                })
                cartBubble.classList.remove("hidden", "hide")
                cartBubble.classList.add("visible", "show")

                cartBubble.classList.add("cart-bubble--animating")
                setTimeout(() => {
                  cartBubble.classList.remove("cart-bubble--animating")
                }, 300)
              } else {
                Object.assign(cartBubble.style, {
                  display: "none",
                  visibility: "hidden",
                  opacity: "0",
                })
                cartBubble.classList.add("hidden", "hide")
                cartBubble.classList.remove("visible", "show")
              }
            }
          }
        })
      } catch (error) {
        console.warn(`Error updating selector ${selector}:`, error)
      }
    })
  }

  /**
   * Handle PREVENTA toggle clicks
   */
  handleToggleClick(event) {
    event.preventDefault()
    event.stopPropagation()

    const toggle = event.currentTarget
    toggle.classList.toggle("clicked")
    this.updateMarcadorProperties(toggle)
  }

  /**
   * Update marcador properties for PREVENTA toggles
   */
  updateMarcadorProperties(toggleElement) {
    const marcadorProducto = toggleElement.closest(".marcador-producto")
    const productCard = toggleElement.closest(".product-card")

    if (!marcadorProducto || !productCard) return

    // Calculate distance from marcador-producto to top of product-card
    const marcadorRect = marcadorProducto.getBoundingClientRect()
    const cardRect = productCard.getBoundingClientRect()
    const distance = marcadorRect.top - cardRect.top

    // Calculate total height needed
    const marcadorHeight = marcadorProducto.offsetHeight
    const totalHeight = marcadorHeight + distance

    // Set CSS custom properties
    toggleElement.style.setProperty("--total-height", `${totalHeight}px`)
    toggleElement.style.setProperty("--top-offset", `${distance}px`)
  }

  /**
   * Update all marcador properties
   */
  updateAllMarcadorProperties() {
    const allToggles = document.querySelectorAll(".marcador-toggle")
    allToggles.forEach((toggle) => this.updateMarcadorProperties(toggle))
  }

  /**
   * Add event listeners to cart buttons and PREVENTA toggles
   */
  addCartListeners() {
    // Remove existing listeners
    const existingButtons = this.container.querySelectorAll(".btn-add-to-cart")
    existingButtons.forEach((button) => {
      button.removeEventListener("click", this.handleAddToCart)
    })

    // Add listeners to all add to cart buttons
    const addToCartButtons = this.container.querySelectorAll(".btn-add-to-cart")

    console.log(`🔗 Adding cart listeners to ${addToCartButtons.length} buttons`)

    addToCartButtons.forEach((button, index) => {
      const variantId = button.getAttribute("data-variant-id")
      const productId = button.getAttribute("data-product-id")

      if (variantId && productId) {
        button.addEventListener("click", this.handleAddToCart)
        console.log(`✅ Added listener to button ${index + 1}:`, {
          variantId,
          productId,
          classes: button.className,
        })
      } else {
        console.warn(`⚠️ Button ${index + 1} missing required data attributes:`, {
          variantId,
          productId,
          button,
        })
      }
    })

    // Add PREVENTA toggle listeners
    const newToggles = this.container.querySelectorAll(".marcador-toggle")
    console.log(`🎯 Adding toggle listeners to ${newToggles.length} markers`)

    newToggles.forEach((toggle, index) => {
      toggle.removeEventListener("click", this.handleToggleClick)
      toggle.addEventListener("click", this.handleToggleClick)
      this.updateMarcadorProperties(toggle)
      console.log(`✅ Added toggle listener to marker ${index + 1}`)
    })
  }

  /**
   * Debug cart buttons
   */
  debugCartButtons() {
    const buttons = this.container.querySelectorAll(".btn-add-to-cart")
    const toggles = this.container.querySelectorAll(".marcador-toggle")

    console.log("=== CART BUTTONS DEBUG ===")
    console.log(`Found ${buttons.length} cart buttons`)
    console.log(`Found ${toggles.length} PREVENTA toggles`)

    buttons.forEach((button, index) => {
      console.log(`Button ${index + 1}:`, {
        variantId: button.getAttribute("data-variant-id"),
        productId: button.getAttribute("data-product-id"),
        quantity: button.getAttribute("data-quantity"),
        productTitle: button.getAttribute("data-product-title"),
        classes: button.className,
        disabled: button.disabled,
        hasClickListener: button.onclick !== null,
      })
    })

    console.log("=== END DEBUG ===")
  }

  /**
   * Get grid width class based on products per row
   */
  getGridWidthClass(productsPerRow) {
    switch (productsPerRow) {
      case 2:
        return "grid-width-2"
      case 3:
        return "grid-width-3"
      case 4:
        return "grid-width-4"
      case 5:
        return "grid-width-5"
      default:
        return "grid-width-4"
    }
  }

  /**
   * Extract price from product
   */
  extractPrice(product) {
    if (typeof product.price === "number") {
      return product.price
    } else if (typeof product.price === "string") {
      return Number.parseFloat(product.price.replace(/[^0-9.]/g, ""))
    } else if (product.variants && product.variants.length > 0) {
      const variantPrice = product.variants[0].price
      if (typeof variantPrice === "number") {
        return variantPrice
      } else if (typeof variantPrice === "string") {
        return Number.parseFloat(variantPrice.replace(/[^0-9.]/g, ""))
      }
    }
    return 0
  }

  /**
   * Extract compare at price from product
   */
  extractCompareAtPrice(product) {
    if (typeof product.compare_at_price_max === "number" && product.compare_at_price_max > 0) {
      return product.compare_at_price_max
    } else if (typeof product.compare_at_price_max === "string" && product.compare_at_price_max !== "") {
      return Number.parseFloat(product.compare_at_price_max.replace(/[^0-9.]/g, ""))
    } else if (typeof product.compare_at_price === "number" && product.compare_at_price > 0) {
      return product.compare_at_price
    } else if (typeof product.compare_at_price === "string" && product.compare_at_price !== "") {
      return Number.parseFloat(product.compare_at_price.replace(/[^0-9.]/g, ""))
    } else if (product.variants && product.variants.length > 0) {
      const variant = product.variants[0]
      if (typeof variant.compare_at_price === "number" && variant.compare_at_price > 0) {
        return variant.compare_at_price
      } else if (typeof variant.compare_at_price === "string" && variant.compare_at_price !== "") {
        return Number.parseFloat(variant.compare_at_price.replace(/[^0-9.]/g, ""))
      }
    }
    return 0
  }

  /**
   * Check if product is available
   */
  isProductAvailable(product) {
    if (typeof product.available === "boolean") {
      return product.available
    }

    if (product.variants && product.variants.length > 0) {
      for (let i = 0; i < product.variants.length; i++) {
        if (product.variants[i].available) {
          return true
        }
      }
      return false
    }

    return true
  }

  /**
   * Format money amount
   */
  formatMoney(amount) {
    if (typeof amount !== "number") {
      amount = Number.parseFloat(String(amount).replace(/[^0-9.]/g, ""))
    }

    if (isNaN(amount) || amount === null) {
      amount = 0
    }

    return this.config.currencySymbol + amount.toFixed(2)
  }

  getCrossSellHandles(product) {
    if (!product || !Array.isArray(product.tags)) return []

    return product.tags
      .filter((tag) => typeof tag === "string")
      .map((tag) => {
        if (tag.startsWith("cross-sale0:")) return tag.slice(11)
        return ""
      })
      .map((tag) => tag.trim())
      .filter(Boolean)
  }

  /**
   * 🎯 UPDATED: Create product card HTML matching your new Liquid structure
   */
  createProductCard(product, index) {
    const gridWidthClass = this.getGridWidthClass(this.config.productsPerRow)
    const firstVariant = product.variants && product.variants.length > 0 ? product.variants[0] : null

    const crossSellHandles = this.getCrossSellHandles(product)
    const crossSellAttribute = crossSellHandles.length ? ` data-cross-sell-handles="${crossSellHandles.join(',')}"` : ""

    // 🚫 Hide sold-out products (matching your Liquid logic)
    if (!this.isProductAvailable(product)) {
      return `<div class="product-card-wrapper ${gridWidthClass}" style="display: none;"></div>`
    }

    let html = `<div class="product-card-wrapper ${gridWidthClass}" style="">`
    html += `<div class="product-card" data-product-id="${product.id}"${crossSellAttribute}>`

    // Main image section
    html += `<a href="/products/${product.handle}" class="product-card__image-link">`
    html += '<div class="product-card__image-wrapper">'

    // Get image URL
    let imageUrl = ""
    if (product.images && product.images.length > 0) {
      imageUrl = product.images[0].src
    } else if (product.featured_image && typeof product.featured_image === "string") {
      imageUrl = product.featured_image
    } else if (product.featured_image && product.featured_image.src) {
      imageUrl = product.featured_image.src
    } else if (product.image && product.image.src) {
      imageUrl = product.image.src
    }

    if (imageUrl) {
      if (imageUrl.indexOf("?") !== -1) {
        imageUrl = imageUrl.split("?")[0] + "?width=600"
      } else {
        imageUrl += "?width=600"
      }

      html += `<img class="product-card__image"
                    src="${imageUrl}"
                    alt="${product.title || "Product"}"
                    loading="lazy"
                    onload="this.style.opacity='1'"
                    style="opacity: 0; transition: opacity 0.3s;">`
    } else {
      html += `<div class="product-card__image product-card__image--placeholder">
                 <svg width="100" height="100" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
                   <rect width="100" height="100" fill="#f8f8f8"/>
                   <path d="M30 40L50 20L70 40V70H30V40Z" fill="#ddd"/>
                   <circle cx="40" cy="35" r="5" fill="#bbb"/>
                 </svg>
               </div>`
    }

    // Add sold-out badge if needed (though we're hiding sold-out products)
    if (!this.isProductAvailable(product)) {
      html += '<div class="product-card__badge sold-out-badge">Sold Out</div>'
    }

    html += "</div></a>"

    // 🎯 UPDATED PREVENTA marker logic with info/close icons
    const hasPreventaTag =
      product.tags && product.tags.some((tag) => tag.toUpperCase() === "PREVENTA" || tag === "PREVENTA")

    if (hasPreventaTag) {
      html += `
        <div class="marcador-producto preventa-marker">
          <div class="marcador-toggle">
            <div class="marcador-title-wrapper">
              <div class="marcador-preventa-title">PRODUCTO EN PREVENTA</div>
              <img
                class="info-icon"
                src="https://cdn.shopify.com/s/files/1/0785/7966/3138/files/steal_sashimi_info_preventa_icon_d23dae32-c77e-43c6-b7c6-f4fa6cffd5c4.svg?v=1752041155"
              >
              <img
                class="close-btn"
                src="https://cdn.shopify.com/s/files/1/0785/7966/3138/files/steal_sashimi_x_mark_toggle.svg?v=1752178779"
              >
            </div>
            <div class="marcador-content-wrapper">
              <div class="marcador-text">
                Esta Sashimi está agotada en inventario físico, pero será producida especialmente para ti. El
                tiempo estimado de entrega es de 3 semanas. Recibirás una notificación por correo una vez que tu
                pedido esté listo para enviarse.
              </div>
              <div class="marcador-text jp">
                こちらのお刺身は在庫がございませんが、特別にお作りさせていただきます。納期の目安は3週間です。ご注文の発送準備が完了すると、電子メールで通知が届きます。
              </div>
            </div>
          </div>
        </div>`
    } else {
      // 🎯 UPDATED: Black marker for available products with info/close icons
      html += `
        <div class="marcador-producto preventa-marker black">
          <div class="marcador-toggle">
            <div class="marcador-title-wrapper">
              <div class="marcador-preventa-title">利用可能な製品</div>
              <img
                class="info-icon"
                src="https://cdn.shopify.com/s/files/1/0785/7966/3138/files/steal_sashimi_info_preventa_icon_white.svg?v=1752131418"
              >
              <img
                class="close-btn"
                src="https://cdn.shopify.com/s/files/1/0785/7966/3138/files/steal_sashimi_x_mark_toggle_white.svg?v=1752178779"
              >
            </div>
            <div class="marcador-content-wrapper">
              <div class="marcador-text">
                ¡AAAAAAA! TUS PASOS FUERON TRAZADOS MUCHO ANTES DE QUE SUPIERAS CAMINAR. SI ESTÁS AQUÍ ES PORQUE
                LOS ASTROS SE JUNTARON. NADA ES CASUALIDAD. LA ESENCIA DEL INVERTEBRADO TOMA VIDA A PARTIR DEL
                MOVIMIENTO, QUE SE VUELVE LA RAZÓN DEL ÉXITO PREDESTINADO. ESPERA… ¿CREES EN EL DESTINO?
              </div>
              <div class="marcador-text jp">
                アアアアアア！君の歩みは、歩き方を知るずっと前から定められていた。ここにいるのは、星々が巡り合ったからだ。偶然など存在しない。
                無脊椎の本質は、動きによって命を得る——それは、定められた成功の理由へと変わる。 待て…
                運命を信じるか？
              </div>
            </div>
          </div>
        </div>`
    }

    // Info section
    html += '<div class="product-card__info">'

    // 🎯 UPDATED: Desktop inline layout (no thumbnail, simplified)
    html += '<div class="product-card__inline-layout">'

    // Center: Title and Price
    html += '<div class="product-card__details">'
    html += `<a href="/products/${product.handle}" class="product-card__details-link">`
    html += `<h3 class="product-card__title">${product.title}</h3>`

    const price = this.extractPrice(product)
    const compareAtPrice = this.extractCompareAtPrice(product)

    html += '<div class="product-card__price">'
    if (compareAtPrice > price && price > 0) {
      html += `<span class="price-compare">${this.formatMoney(compareAtPrice)}</span>`
      html += `<span class="price-sale">${this.formatMoney(price)}</span>`
    } else {
      html += `<span class="price-regular">${this.formatMoney(price)}</span>`
    }
    html += "</div></a></div>"

    // Right: Add to Cart Button
    html += '<div class="product-card__quick-add">'
    if (this.isProductAvailable(product) && firstVariant) {
      if (product.variants.length === 1) {
        html += `<button type="button" class="btn-quick-add btn-add-to-cart" data-product-id="${product.id}" data-variant-id="${firstVariant.id}" data-product-title="${product.title.replace(/"/g, "&quot;")}" data-quantity="1" ${!firstVariant.available ? "disabled" : ""}>`
        html += `<img class="btn-quick-add__image" src="https://cdn.shopify.com/s/files/1/0785/7966/3138/files/steal_sashimi_cart_icon_v3.svg" alt="Add to Cart" loading="lazy">`
        html += "</button>"
      } else {
        html += `<a href="/products/${product.handle}" class="btn-quick-add btn-select-options">`
        html += `<img class="btn-quick-add__image" src="https://cdn.shopify.com/s/files/1/0785/7966/3138/files/steal_sashimi_cart_icon_v3.svg" alt="Select Options" loading="lazy">`
        html += "</a>"
      }
    } else {
      html += '<button class="btn-quick-add btn-sold-out" disabled>'
      html += `<img class="btn-quick-add__image" src="https://cdn.shopify.com/s/files/1/0785/7966/3138/files/steal_sashimi_cart_icon_v3.svg" alt="Sold Out" loading="lazy" style="opacity: 0.5; filter: grayscale(100%);">`
      html += "</button>"
    }
    html += "</div>"
    html += "</div>" // Close inline layout

    // 🎯 UPDATED: Mobile Only - Thumbnail + Quick Add Row
    html += '<div class="product-card__mobile-row">'

    // Left: Decorative thumbnail for mobile
    html += '<div class="product-card__mobile-thumbnail">'
    html += `<a href="/products/${product.handle}">`
    html += `<img class="product-card__mobile-thumbnail-image" src="https://cdn.shopify.com/s/files/1/0785/7966/3138/files/steal_sashimi_card_product.svg?v=1751173217" alt="Product Icon" loading="lazy">`
    html += "</a></div>"

    // Right: Mobile Quick Add Button
    html += '<div class="product-card__mobile-quick-add">'
    if (this.isProductAvailable(product) && firstVariant) {
      if (product.variants.length === 1) {
        html += `<button type="button" class="btn-quick-add-mobile btn-add-to-cart" data-product-id="${product.id}" data-variant-id="${firstVariant.id}" data-product-title="${product.title.replace(/"/g, "&quot;")}" data-quantity="1" ${!firstVariant.available ? "disabled" : ""}>`
        html += `<img class="btn-quick-add-mobile__image" src="https://cdn.shopify.com/s/files/1/0785/7966/3138/files/steal_sashimi_cart_icon_v3.svg" alt="Add to Cart" loading="lazy">`
        html += "</button>"
      } else {
        html += `<a href="/products/${product.handle}" class="btn-quick-add-mobile btn-select-options-mobile">`
        html += `<img class="btn-quick-add-mobile__image" src="https://cdn.shopify.com/s/files/1/0785/7966/3138/files/steal_sashimi_cart_icon_v3.svg" alt="Select Options" loading="lazy">`
        html += "</a>"
      }
    } else {
      html += '<button class="btn-quick-add-mobile btn-sold-out-mobile" disabled>'
      html += `<img class="btn-quick-add-mobile__image" src="https://cdn.shopify.com/s/files/1/0785/7966/3138/files/steal_sashimi_cart_icon_v3.svg" alt="Sold Out" loading="lazy" style="opacity: 0.5; filter: grayscale(100%);">`
      html += "</button>"
    }
    html += "</div></div>"

    // 🎯 UPDATED: Mobile Only - Product Details (moved below mobile row)
    html += '<div class="product-card__mobile-details">'
    html += `<a href="/products/${product.handle}" class="product-card__mobile-details-link">`
    html += `<h3 class="product-card__mobile-title">${product.title}</h3>`
    html += '<div class="product-card__mobile-price">'
    if (compareAtPrice > price && price > 0) {
      html += `<span class="price-compare">${this.formatMoney(compareAtPrice)}</span>`
      html += `<span class="price-sale">${this.formatMoney(price)}</span>`
    } else {
      html += `<span class="price-regular">${this.formatMoney(price)}</span>`
    }
    html += "</div></a></div>"

    // Bottom "Comprar ahora" Button
    html += '<div class="product-card__bottom-action">'
    if (this.isProductAvailable(product) && firstVariant) {
      if (product.variants.length === 1) {
        html += `<button type="button" class="btn-add-to-cart btn-add-to-cart-mobile" data-product-id="${product.id}" data-variant-id="${firstVariant.id}" data-product-title="${product.title.replace(/"/g, "&quot;")}" data-quantity="1" ${!firstVariant.available ? "disabled" : ""}>`
        html += '<span class="btn-comprar-ahora__text">Agregar al carrito</span>'
        html += "</button>"
        html += `<button type="button" class="btn-comprar-ahora btn-add-to-cart" data-product-id="${product.id}" data-variant-id="${firstVariant.id}" data-product-title="${product.title.replace(/"/g, "&quot;")}" data-quantity="1" ${!firstVariant.available ? "disabled" : ""}>`
        html += '<span class="btn-comprar-ahora__text">Comprar ahora</span>'
        html += "</button>"
      } else {
        html += `<a href="/products/${product.handle}" class="btn-comprar-ahora btn-select-options-bottom">`
        html += '<span class="btn-comprar-ahora__text">Ver opciones</span>'
        html += "</a>"
      }
    } else {
      html += '<button class="btn-comprar-ahora btn-sold-out-bottom" disabled>'
      html += '<span class="btn-comprar-ahora__text">Agotado</span>'
      html += "</button>"
    }
    html += "</div>"

    html += "</div></div></div>" // Close info, card, wrapper

    return html
  }

  /**
   * Show skeleton loaders
   */
  showSkeletonLoaders() {
    if (this.elements.skeletonContainer) {
      this.elements.skeletonContainer.style.display = "flex"
    }
  }

  /**
   * Hide skeleton loaders
   */
  hideSkeletonLoaders() {
    if (this.elements.skeletonContainer) {
      this.elements.skeletonContainer.style.display = "none"
    }
  }

  /**
   * Load more products
   */
  loadMoreProducts() {
    if (this.state.isLoading || !this.state.hasMoreProducts) return

    this.state.isLoading = true
    this.elements.loadingElement.style.display = "block"
    this.elements.errorElement.style.display = "none"
    this.showSkeletonLoaders()

    const url = `/collections/${this.config.collectionHandle}/products.json?limit=${this.config.productsPerPage}&page=${this.state.currentPage + 1}`

    console.log(`📄 Loading page ${this.state.currentPage + 1} from: ${url}`)

    fetch(url)
      .then((response) => {
        if (!response.ok) {
          throw new Error("Network response was not ok")
        }
        return response.json()
      })
      .then((data) => {
        this.hideSkeletonLoaders()

        const products = data.products

        if (!products || products.length === 0) {
          this.state.hasMoreProducts = false
          console.log("📭 No more products found")
        } else {
          console.log(`📦 Loaded ${products.length} products`)

          let productsHTML = ""
          let visibleProductsCount = 0

          products.forEach((product, index) => {
            const productHTML = this.createProductCard(
              product,
              this.state.currentPage * this.config.productsPerPage + index,
            )
            if (productHTML) {
              productsHTML += productHTML
              // Only count visible products (not hidden sold-out ones)
              if (this.isProductAvailable(product)) {
                visibleProductsCount++
              }
            }
          })

          if (productsHTML) {
            const tempDiv = document.createElement("div")
            tempDiv.innerHTML = productsHTML

            while (tempDiv.firstChild) {
              this.elements.productsGrid.appendChild(tempDiv.firstChild)
            }

            // 🎯 IMPORTANT: Re-add listeners after adding new products
            this.addCartListeners()
            console.log(`✅ Added ${visibleProductsCount} visible products and re-initialized listeners`)
          }

          this.state.currentPage++
        }

        this.state.isLoading = false
        this.elements.loadingElement.style.display = "none"
      })
      .catch((error) => {
        console.error("❌ Error loading more products:", error)
        this.hideSkeletonLoaders()
        this.elements.errorElement.style.display = "block"
        this.state.isLoading = false
        this.elements.loadingElement.style.display = "none"
      })
  }

  /**
   * Check if element is in viewport
   */
  isInViewport(element) {
    const rect = element.getBoundingClientRect()
    return (
      rect.bottom > 0 &&
      rect.right > 0 &&
      rect.left < (window.innerWidth || document.documentElement.clientWidth) &&
      rect.top < (window.innerHeight || document.documentElement.clientHeight) * 1.5
    )
  }

  /**
   * Scroll event handler
   */
  onScroll() {
    if (this.isInViewport(this.elements.statusElement) && !this.state.isLoading && this.state.hasMoreProducts) {
      this.loadMoreProducts()
    }
  }
}

// Initialize components when DOM is loaded
document.addEventListener("DOMContentLoaded", () => {
  console.log("🚀 Initializing Collection Infinite Scroll components...")

  // Find all collection infinite scroll blocks and initialize them
  const collectionBlocks = document.querySelectorAll('[id^="CollectionInfiniteScroll-"]')

  console.log(`📋 Found ${collectionBlocks.length} collection blocks`)

  collectionBlocks.forEach((block, index) => {
    console.log(`🔧 Initializing block ${index + 1}: ${block.id}`)
    new CollectionInfiniteScroll(block.id)
  })
})
